import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import './ServiceTemplate.css';

export default function ServiceTemplate({ data }) {
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => entries.forEach(e => { if (e.isIntersecting) e.target.classList.add('visible'); }),
      { threshold: 0.1 }
    );
    document.querySelectorAll('.st-animate').forEach(el => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  return (
    <div className="service-page">

      {/* HERO */}
      <section className="page-hero">
        <div className="container page-hero-content hero-animate">
          <span className="section-tag">{data.tagline}</span>
          <h1>{data.title} <span>{data.subtitle}</span></h1>
          <p>{data.heroDesc}</p>
          <div className="st-hero-cta">
            <Link to="/contact" className="btn-primary">Get Free Consultation →</Link>
          </div>
        </div>
      </section>

      {/* STATS BAR */}
      <section className="st-stats-bar">
        <div className="container">
          <div className="st-stats-grid">
            <div className="st-main-stat">
              <span>{data.mainStat}</span>
              <p>{data.mainStatLabel}</p>
            </div>
            {data.subStats.map((s, i) => (
              <div className="st-sub-stat" key={i}>
                <span>{s.value}</span>
                <p>{s.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FEATURES */}
      <section className="st-section">
        <div className="container">
          <div className="st-section-header st-animate">
            <span className="section-tag">What We Offer</span>
            <h2 className="section-title">Our <span>{data.title} {data.subtitle}</span> Services</h2>
          </div>
          <div className="st-features-grid">
            {data.features.map((f, i) => (
              <div className="st-feature-card st-animate" key={i}>
                <div className="st-feature-icon">{f.icon}</div>
                <h3>{f.title}</h3>
                <p>{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* BENEFITS */}
      <section className="st-section st-alt">
        <div className="container">
          <div className="st-section-header st-animate">
            <span className="section-tag">Why It Matters</span>
            <h2 className="section-title">Key <span>Benefits</span></h2>
          </div>
          <div className="st-benefits-grid">
            {data.benefits.map((b, i) => (
              <div className="st-benefit-card st-animate" key={i}>
                <div className="st-benefit-num">0{i + 1}</div>
                <h3>{b.title}</h3>
                <p>{b.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PROCESS */}
      <section className="st-section">
        <div className="container">
          <div className="st-section-header st-animate">
            <span className="section-tag">How We Work</span>
            <h2 className="section-title">Our <span>Process</span></h2>
          </div>
          <div className="st-process-list">
            {data.process.map((step, i) => (
              <div className="st-process-step st-animate" key={i}>
                <div className="st-step-num">{String(i + 1).padStart(2, '0')}</div>
                <div className="st-step-content">
                  <h3>{step.title}</h3>
                  <p>{step.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* WHY CHOOSE */}
      <section className="st-section st-alt">
        <div className="container">
          <div className="st-section-header st-animate">
            <span className="section-tag">Why DMS</span>
            <h2 className="section-title">Why Choose <span>Us</span></h2>
          </div>
          <div className="st-why-grid">
            {data.whyChoose.map((w, i) => (
              <div className="st-why-card st-animate" key={i}>
                <div className="st-why-icon">{w.icon}</div>
                <h3>{w.title}</h3>
                <p>{w.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* REVIEWS */}
      <section className="st-section">
        <div className="container">
          <div className="st-section-header st-animate">
            <span className="section-tag">Client Stories</span>
            <h2 className="section-title">What Our <span>Clients Say</span></h2>
          </div>
          <div className="st-reviews-grid">
            {data.reviews.map((r, i) => (
              <div className="st-review-card st-animate" key={i}>
                <div className="st-stars">★★★★★</div>
                <p>"{r.text}"</p>
                <div className="st-reviewer">
                  <div className="st-avatar">{r.name[0]}</div>
                  <div>
                    <strong>{r.name}</strong>
                    <span>{r.role}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="st-section st-alt">
        <div className="container">
          <div className="st-section-header st-animate">
            <span className="section-tag">FAQ</span>
            <h2 className="section-title">Frequently Asked <span>Questions</span></h2>
          </div>
          <div className="st-faq-list">
            {data.faq.map((f, i) => (
              <div className="st-faq-item st-animate" key={i}>
                <h4>Q: {f.q}</h4>
                <p>{f.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA BANNER */}
      <section className="st-cta-section">
        <div className="container">
          <div className="st-cta-box st-animate">
            <h2>Ready to Get Started with <span>{data.title} {data.subtitle}</span>?</h2>
            <p>Let's talk about your goals and build a strategy that delivers real results.</p>
            <div className="st-cta-actions">
              <Link to="/contact" className="btn-primary">Get Free Consultation →</Link>
              <a href="https://wa.me/919370115694" target="_blank" rel="noreferrer" className="btn-secondary">Chat on WhatsApp 💬</a>
            </div>
          </div>
        </div>
      </section>

    </div>
  );
}
